import {toValue, type MaybeRef, computed} from 'vue'
import {useUserStore} from '../stores/user'
import {useCompanyStore} from '../stores/company'

export const DECIMAL_FMT: Intl.NumberFormatOptions = {style: 'decimal', maximumFractionDigits: 2}
export const DIGIT_FMT: Intl.NumberFormatOptions = {style: 'decimal', maximumFractionDigits: 0}
export const PERCENT_FMT: Intl.NumberFormatOptions = {style: 'percent', maximumFractionDigits: 1}
export const PERCENT_DIGIT_FMT: Intl.NumberFormatOptions = {style: 'percent', maximumFractionDigits: 0}
export const CURRENCY_FMT: Intl.NumberFormatOptions = {...DIGIT_FMT, style: 'currency'}

/**
 * Provides a NumberFormatter based on given options and stored user properties
 * @param options NumberFormatOptions
 * @param locale Optional string of language/locale to use (default: user locale)
 */
export function useNumberFormatter(options: MaybeRef<Intl.NumberFormatOptions>, locale?: MaybeRef<string>) {
  const companyStore = useCompanyStore()
  const userStore = useUserStore()

  const modOptions = computed(() =>
    toValue(options).style === 'currency' && companyStore.currency
      ? {currency: companyStore.currency, ...toValue(options)}
      : toValue(options)
  )
  const Formatter = computed(() => Intl.NumberFormat(toValue(locale) ?? userStore.locale, modOptions.value))

  return [Formatter]
}

/**
 * Returns an ExcelJS numFmt string for the company currency with no grouping separator.
 * E.g. for EUR: '"€"1230'
 */
export function useXlsxCurrencyNumFmt(): string {
  const companyStore = useCompanyStore()
  const userStore = useUserStore()
  if (!companyStore.currency) return '0'
  const parts = new Intl.NumberFormat(userStore.locale, {
    style: 'currency',
    currency: companyStore.currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).formatToParts(0)
  const symIdx = parts.findIndex((p) => p.type === 'currency')
  const intIdx = parts.findIndex((p) => p.type === 'integer')
  const symbol = parts[symIdx]?.value ?? ''
  return symIdx < intIdx ? `"${symbol}"0.00` : `0.00"${symbol}"`
}
